(() => {
  const SDK_URL = 'https://api.tapcart.com/wishlist/sdk/v1/wishlist-sdk.js';
  const CATALOG_QUERY = `query WishlistProducts($ids: [ID!]!, $country: CountryCode!, $language: LanguageCode!)
    @inContext(country: $country, language: $language) {
      nodes(ids: $ids) {
        id
        ... on Product { handle onlineStoreUrl availableForSale productType }
        ... on ProductVariant { availableForSale product { id } }
      }
    }`;
  let sdkPromise;
  let instanceCount = 0;

  function timeout(promise, duration = 20000) {
    let timer;
    return Promise.race([
      promise,
      new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('timeout')), duration); }),
    ]).finally(() => clearTimeout(timer));
  }

  function loadSDK() {
    if (window.TapcartWishlist?.init) return Promise.resolve(window.TapcartWishlist);
    if (!sdkPromise) {
      sdkPromise = timeout(new Promise((resolve, reject) => {
        let script = Array.from(document.scripts).find((entry) => entry.src === SDK_URL);
        const owned = !script;
        if (owned) {
          script = document.createElement('script');
          script.src = SDK_URL;
          script.async = true;
          script.dataset.wishlistSdk = '';
        }
        script.addEventListener('load', () => {
          if (window.TapcartWishlist?.init) resolve(window.TapcartWishlist);
          else reject(new Error('sdk'));
        }, { once: true });
        script.addEventListener('error', () => reject(new Error('sdk')), { once: true });
        if (owned) document.head.append(script);
      })).catch((error) => {
        document.querySelector('script[data-wishlist-sdk]')?.remove();
        sdkPromise = null;
        throw error;
      });
    }
    return sdkPromise;
  }

  class WishlistContractError extends Error { name = 'WishlistContractError'; }
  class WishlistGuestError extends Error { name = 'WishlistGuestError'; }
  class WishlistListConflictError extends Error { name = 'WishlistListConflictError'; }

  class WishlistPage extends HTMLElement {
    static translationText(value) {
      const entities = { amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', '#39': "'", '#x27': "'" };
      return value.replace(/&(amp|quot|apos|lt|gt|#39|#x27);/gi, (_, entity) => entities[entity.toLowerCase()]);
    }

    static suppressVendorDecoration(root) {
      root.setAttribute('data-tcwl-done', 'wishlist');
      for (const anchor of root.querySelectorAll('a[href*="/products/"]')) {
        anchor.setAttribute('data-tcwl-done', 'wishlist');
        const card = anchor.closest('.card, .product-card, .grid__item, .product-item, li, article');
        if (card && root.contains(card)) card.setAttribute('data-tcwl-done', 'wishlist');
      }
    }

    static async request(url, options, consume, duration = 20000) {
      const controller = new AbortController();
      const callerSignal = options.signal;
      let rejectInterrupted;
      const interrupted = new Promise((_, reject) => { rejectInterrupted = reject; });
      const abort = (name) => {
        if (controller.signal.aborted) return;
        const error = new Error(name === 'TimeoutError' ? 'Wishlist request timed out' : 'Wishlist request cancelled');
        error.name = name;
        rejectInterrupted(error);
        controller.abort();
      };
      const onCallerAbort = () => abort('AbortError');
      const timer = setTimeout(() => abort('TimeoutError'), duration);
      callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
      try {
        if (callerSignal?.aborted) {
          onCallerAbort();
          return await interrupted;
        }
        return await Promise.race([
          (async () => consume(await fetch(url, { ...options, signal: controller.signal })))(),
          interrupted,
        ]);
      } finally {
        clearTimeout(timer);
        callerSignal?.removeEventListener('abort', onCallerAbort);
      }
    }

    static localeRoot(value) {
      if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) throw new WishlistContractError();
      return `${value.replace(/\/+$/, '')}/`;
    }

    static opaqueId(value) {
      if (typeof value === 'number' && !Number.isSafeInteger(value)) throw new WishlistContractError();
      if (!['string', 'number'].includes(typeof value) || !String(value).trim()) throw new WishlistContractError();
      return String(value);
    }

    static recordId(record) {
      const id = WishlistPage.opaqueId(record?._id === undefined ? record?.id : record._id);
      if (record._id !== undefined && record.id !== undefined && WishlistPage.opaqueId(record.id) !== id) {
        throw new WishlistContractError();
      }
      return id;
    }

    static shopifyId(value, type) {
      const raw = WishlistPage.opaqueId(value);
      const decimal = raw.startsWith(`gid://shopify/${type}/`) ? raw.split('/').pop() : raw;
      if (!/^[1-9]\d*$/.test(decimal)) throw new WishlistContractError();
      return { decimal, gid: `gid://shopify/${type}/${decimal}` };
    }

    static audience(type = '') {
      const normalized = type.toUpperCase();
      if (/\bKIDS\b/.test(normalized)) return 'kids';
      if (/\bBIG\s?MEN\b/.test(normalized)) return 'bigMen';
      if (/\bMEN(?:'S|S)?\b/.test(normalized)) return 'men';
      return 'other';
    }

    static async readLists(client) {
      const lists = [];
      const seen = new Set();
      let page = 1;
      let expectedPages;
      let expectedTotal;
      do {
        const response = await timeout(client.getWishlists({ page, limit: 25 }));
        if (response === null) throw new WishlistGuestError();
        const pagination = response?.pagination;
        if (!Array.isArray(response?.data) || !pagination ||
          !['page', 'limit', 'total', 'totalPages'].every((key) => Number.isSafeInteger(pagination[key])) ||
          pagination.page !== page || pagination.limit < 1 || pagination.total < 0 ||
          pagination.totalPages < 0 || pagination.totalPages > 1000 ||
          (page > 1 && (pagination.totalPages !== expectedPages || pagination.total !== expectedTotal))) {
          throw new WishlistContractError();
        }
        expectedPages = pagination.totalPages;
        expectedTotal = pagination.total;
        if (response.data.length > pagination.limit ||
          (expectedTotal > 0 && expectedPages < 1) || (page < expectedPages && response.data.length === 0)) {
          throw new WishlistContractError();
        }
        for (const list of response.data) {
          const id = WishlistPage.recordId(list);
          if (seen.has(id) || typeof list.name !== 'string') throw new WishlistContractError();
          seen.add(id);
          lists.push({ id, name: list.name });
        }
        page += 1;
      } while (page <= expectedPages);
      if (lists.length !== expectedTotal) throw new WishlistContractError();
      return lists;
    }

    static async readItems(client, listId) {
      const response = await timeout(client.getWishlist(listId));
      if (response === null) throw new WishlistGuestError();
      // Small authenticated lists use direct objects with _id. Large-list completeness still gates rollout.
      if (!response || WishlistPage.recordId(response) !== listId || !Array.isArray(response.items) ||
        (response.pagination && (response.pagination.totalPages > 1 ||
          response.pagination.total !== response.items.length))) throw new WishlistContractError();
      const seen = new Set();
      return response.items.map((item) => {
        const id = WishlistPage.recordId(item);
        if (seen.has(id)) throw new WishlistContractError();
        seen.add(id);
        return {
          id,
          listId,
          product: WishlistPage.shopifyId(item.productId, 'Product'),
          variant: item.variantId == null ? null : WishlistPage.shopifyId(item.variantId, 'ProductVariant'),
        };
      });
    }

    connectedCallback() {
      if (this.life) return;
      this.config = JSON.parse(this.querySelector('[data-wishlist-config]').textContent);
      this.config.rootUrl = WishlistPage.localeRoot(this.config.rootUrl);
      this.config.country = this.config.country.toUpperCase();
      this.config.language = this.config.language.toUpperCase().replaceAll('-', '_');
      this.wishlistPrefix = `wishlist-${++instanceCount}`;
      this.life = new AbortController();
      this.generation = 0;
      this.ownedModals = new Set();
      this.lists = [];
      this.items = [];
      this.products = new Map();
      this.page = 1;
      this.busy = false;
      this.confirmedGuest = false;
      this.addEventListener('click', (event) => this.onClick(event), { signal: this.life.signal });
      this.querySelector('[data-wishlist-lists]')?.addEventListener('change', (event) => {
        this.selectedId = event.target.value;
        this.audience = null;
        this.refresh();
      }, { signal: this.life.signal });
      this.querySelector('[data-wishlist-list-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.mutateList();
      }, { signal: this.life.signal });
      window.addEventListener('focus', () => this.revalidate(), { signal: this.life.signal });
      document.addEventListener('visibilitychange', () => {
        if (!this.config.enabled) return;
        if (document.hidden) {
          this.invalidate();
          this.state(this.customerAuthenticated() && !this.confirmedGuest ? 'loading' : 'guest');
        } else this.revalidate();
      }, { signal: this.life.signal });
      this.querySelector('[data-wishlist-dialog]')?.addEventListener('close', () => {
        if (this.needsRevalidation) this.revalidate();
      }, { signal: this.life.signal });
      if (!this.config.enabled) this.state('disabled');
      else if (this.customerAuthenticated()) this.refresh();
      else this.state('guest');
    }

    disconnectedCallback() {
      this.invalidate();
      this.life?.abort();
      this.life = null;
      this.querySelector('[data-wishlist-dialog]')?.close();
    }

    text(key, variables = {}) {
      let value = WishlistPage.translationText(this.config.strings[key] || '');
      for (const [name, replacement] of Object.entries(variables)) {
        value = value.replaceAll(`{${name}}`, String(replacement)).replaceAll(`__${name}__`, String(replacement));
      }
      return value;
    }

    state(value, message = '') {
      this.dataset.wishlistState = value;
      this.setAttribute('aria-busy', String(value === 'loading'));
      for (const name of ['disabled', 'guest', 'empty', 'error']) {
        const element = this.querySelector(`[data-wishlist-${name}]`);
        if (element) element.hidden = name !== value;
      }
      this.querySelector('[data-wishlist-error-message]').textContent = value === 'error' ? message : '';
      const status = this.querySelector('[data-wishlist-status]');
      status.hidden = !['loading', 'ready', 'empty'].includes(value);
      status.textContent = value === 'loading' ? this.text('loading') : '';
      this.querySelector('[data-wishlist-controls]').hidden = !['ready', 'empty'].includes(value);
      this.querySelector('[data-wishlist-audiences]').hidden = value !== 'ready';
      const refresh = this.querySelector('[data-wishlist-refresh]');
      if (refresh) refresh.hidden = true;
      if (!['ready', 'empty'].includes(value)) this.querySelector('[data-wishlist-pagination]').hidden = true;
    }

    clearCards() {
      for (const modal of this.ownedModals || []) {
        modal.hide?.();
        modal.remove();
      }
      this.ownedModals?.clear();
      this.querySelector('[data-wishlist-grid]')?.replaceChildren();
    }

    invalidate() {
      this.generation += 1;
      this.readAbort?.abort();
      this.clearCards();
      this.items = [];
      this.products = new Map();
      this.lists = [];
      this.client = null;
      this.querySelector('[data-wishlist-lists]')?.replaceChildren();
      this.querySelector('[data-wishlist-audiences]')?.replaceChildren();
    }

    revalidate() {
      if (!this.config.enabled) return;
      if (!this.customerAuthenticated() || this.confirmedGuest) {
        this.state('guest');
        return;
      }
      if (this.busy || this.querySelector('[data-wishlist-dialog]')?.open) {
        this.needsRevalidation = true;
        return;
      }
      this.needsRevalidation = false;
      this.refresh();
    }

    async refresh() {
      if (!this.config.enabled || !this.isConnected) return;
      const selectedId = this.selectedId;
      this.invalidate();
      const generation = this.generation;
      this.readAbort = new AbortController();
      this.page = 1;
      this.state('loading');
      try {
        const sdk = await loadSDK();
        const client = sdk.init({ mode: 'theme' });
        const lists = await WishlistPage.readLists(client);
        if (generation !== this.generation) return;
        this.client = client;
        this.lists = lists;
        this.selectedId = lists.find((list) => list.name === this.preferredListName)?.id ||
          lists.find((list) => list.id === selectedId)?.id || lists[0]?.id;
        this.preferredListName = null;
        this.renderLists();
        if (!this.selectedId) {
          this.state('empty');
          this.querySelector('[data-wishlist-empty-message]').textContent = this.text('noLists');
          return;
        }
        const items = await WishlistPage.readItems(client, this.selectedId);
        const products = await this.catalog(items, this.readAbort.signal);
        if (generation !== this.generation) return;
        this.items = items;
        this.products = products;
        this.renderAudiences();
        await this.renderPage(generation);
      } catch (error) {
        if (generation !== this.generation) return;
        if (error instanceof WishlistGuestError) this.confirmedGuest = true;
        this.invalidate();
        this.state(error instanceof WishlistGuestError ? 'guest' : 'error',
          this.text(error instanceof WishlistContractError ? 'contractError' : 'loadError'));
      }
    }

    customerAuthenticated() {
      return this.getAttribute('data-customer-authenticated') === 'true';
    }

    async catalog(items, signal) {
      const ids = [...new Set(items.flatMap((item) => [item.product.gid, item.variant?.gid].filter(Boolean)))];
      const products = new Map();
      if (!/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(this.config.storeDomain)) throw new WishlistContractError();
      for (let offset = 0; offset < ids.length; offset += 100) {
        const body = await WishlistPage.request(`https://${this.config.storeDomain}/api/2026-10/graphql.json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal,
          body: JSON.stringify({ query: CATALOG_QUERY, variables: {
            ids: ids.slice(offset, offset + 100), country: this.config.country, language: this.config.language,
          } }),
        }, (response) => {
          if (!response.ok) throw new Error('catalog');
          return response.json();
        });
        if (body.errors?.length || !Array.isArray(body.data?.nodes)) throw new Error('catalog');
        for (const node of body.data.nodes) if (node) products.set(node.id, node);
      }
      return products;
    }

    renderLists() {
      const select = this.querySelector('[data-wishlist-lists]');
      select.replaceChildren(...this.lists.map((list) => {
        const option = document.createElement('option');
        option.value = list.id;
        option.textContent = list.name;
        option.selected = list.id === this.selectedId;
        return option;
      }));
      for (const action of ['rename', 'delete']) this.querySelector(`[data-wishlist-${action}]`).disabled = !this.selectedId;
      select.disabled = this.lists.length === 0;
    }

    renderAudiences() {
      const counts = new Map();
      for (const item of this.items) {
        const audience = WishlistPage.audience(this.products.get(item.product.gid)?.productType);
        counts.set(audience, (counts.get(audience) || 0) + 1);
      }
      if (!counts.has(this.audience)) this.audience = ['men', 'bigMen', 'kids', 'other'].find((key) => counts.has(key));
      this.querySelector('[data-wishlist-audiences]').replaceChildren(...['men', 'bigMen', 'kids', 'other']
        .filter((key) => counts.has(key)).map((key) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'wishlist__audience button button--secondary';
          button.dataset.wishlistAudience = key;
          button.setAttribute('aria-pressed', String(this.audience === key));
          button.textContent = `${this.text(`audience${key[0].toUpperCase()}${key.slice(1)}`)} (${counts.get(key)})`;
          return button;
        }));
    }

    async renderPage(generation = this.generation) {
      this.clearCards();
      this.state('loading');
      const items = this.items.filter((item) => WishlistPage.audience(this.products.get(item.product.gid)?.productType) === this.audience);
      const totalPages = Math.max(1, Math.ceil(items.length / this.config.pageSize));
      this.page = Math.min(this.page, totalPages);
      const visible = items.slice((this.page - 1) * this.config.pageSize, this.page * this.config.pageSize);
      visible.sort((a, b) => Number(!!this.products.get(b.product.gid)?.availableForSale) - Number(!!this.products.get(a.product.gid)?.availableForSale));
      let index = 0;
      let partialError = false;
      const cards = new Array(visible.length);
      const workers = Array.from({ length: Math.min(4, visible.length) }, async () => {
        while (index < visible.length) {
          if (generation !== this.generation) return;
          const position = index++;
          const item = visible[position];
          try { cards[position] = await this.card(item, position, this.readAbort.signal); }
          catch (error) {
            if (error.name === 'AbortError' && (generation !== this.generation || this.readAbort.signal.aborted)) return;
            partialError = true;
            cards[position] = this.placeholder(item, this.text('partialError'));
          }
        }
      });
      await Promise.all(workers);
      if (generation !== this.generation || !this.isConnected) return;
      const list = document.createElement('product-list');
      list.className = 'product-list';
      list.setAttribute('collection-mobile-layout', this.config.mobileLayout);
      list.setAttribute('collection-desktop-layout', this.config.desktopLayout);
      for (const card of cards) if (card) list.append(card);
      for (const modal of list.querySelectorAll('wishlist-quick-buy-modal')) {
        this.ownedModals.add(modal);
        modal.addEventListener('wishlist:quick-buy-error', () => {
          this.querySelector('[data-wishlist-status]').textContent = this.text('loadError');
        }, { signal: this.life.signal });
      }
      this.querySelector('[data-wishlist-grid]').replaceChildren(list);
      this.state(items.length ? 'ready' : 'empty');
      this.querySelector('[data-wishlist-empty-message]').textContent = this.text('empty');
      this.querySelector('[data-wishlist-status]').textContent = partialError ? this.text('partialError') : this.text('itemsCount', { count: items.length });
      const refresh = this.querySelector('[data-wishlist-refresh]');
      if (refresh) refresh.hidden = !partialError;
      const pagination = this.querySelector('[data-wishlist-pagination]');
      pagination.hidden = totalPages < 2;
      this.querySelector('[data-wishlist-previous]').disabled = this.page <= 1;
      this.querySelector('[data-wishlist-next]').disabled = this.page >= totalPages;
      this.querySelector('[data-wishlist-page]').textContent = this.text('page', { current: this.page, total: totalPages });
    }

    async card(item, position, signal) {
      const product = this.products.get(item.product.gid);
      if (!product?.onlineStoreUrl || !product.handle) return this.placeholder(item, this.text('unavailable'));
      const variant = item.variant && this.products.get(item.variant.gid);
      const validVariant = !item.variant || (variant?.product?.id === item.product.gid && variant.availableForSale);
      const url = new URL(`${this.config.rootUrl}products/${encodeURIComponent(product.handle)}`, window.location.origin);
      url.searchParams.set('section_id', 'wishlist-product-card');
      if (item.variant && validVariant) url.searchParams.set('variant', item.variant.decimal);
      const { response, html } = await WishlistPage.request(url, {
        signal, credentials: 'same-origin', cache: 'no-store',
      }, async (response) => ({ response, html: response.ok ? await response.text() : null }));
      if (response.status === 404) return this.placeholder(item, this.text('unavailable'));
      if (!response.ok) throw new Error('card');
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fragment = doc.querySelector('[data-wishlist-product]');
      if (!fragment || fragment.dataset.productId !== item.product.decimal) throw new WishlistContractError();
      const card = fragment.querySelector('product-card');
      if (!card) throw new WishlistContractError();
      WishlistPage.suppressVendorDecoration(card);
      const context = `${this.wishlistPrefix}-${this.generation}-${position}`;
      this.namespace(card, context);
      card.removeAttribute('reveal-on-scroll');
      for (const image of card.querySelectorAll('img[sizes]')) image.sizes = this.config.imageSizes;
      const wrapper = document.createElement('div');
      wrapper.className = 'wishlist__item';
      wrapper.dataset.wishlistItem = item.id;
      wrapper.append(card);
      const title = card.querySelector('.product-title')?.textContent.trim() || product.handle;
      const media = card.querySelector('.product-card__figure');
      if (media) media.append(this.heartButton(item, title));
      if (item.variant && !validVariant) {
        card.querySelector('form[is="product-form"]')?.remove();
        const notice = document.createElement('p');
        notice.className = 'wishlist__notice';
        notice.textContent = this.text('variantUnavailable');
        wrapper.append(notice);
      }
      let modal = card.querySelector('quick-buy-modal');
      if (modal) {
        await this.waitForQuickBuy(signal);
        const wishlistModal = document.createElement('wishlist-quick-buy-modal');
        for (const attribute of modal.attributes) wishlistModal.setAttribute(attribute.name, attribute.value);
        modal.replaceWith(wishlistModal);
        modal = wishlistModal;
        modal.setAttribute('data-wishlist-context', context);
        modal.setAttribute('data-wishlist-choose-options', this.text('chooseOptions'));
        if (item.variant && !validVariant) modal.setAttribute('require-selection', '');
        if (item.variant && validVariant) modal.setAttribute('preferred-variant', item.variant.decimal);
      }
      if (item.variant && validVariant) {
        if (fragment.dataset.selectedVariantId !== item.variant.decimal) throw new WishlistContractError();
        const form = card.querySelector('form[is="product-form"]');
        if (form) form.querySelector('[name="id"]').value = item.variant.decimal;
        for (const link of card.querySelectorAll('a.product-title, a.product-card__media')) {
          const destination = new URL(link.getAttribute('href'), window.location.origin);
          destination.searchParams.set('variant', item.variant.decimal);
          link.setAttribute('href', `${destination.pathname}${destination.search}`);
        }
      }
      wrapper.append(this.removeButton(item, title));
      return wrapper;
    }

    async waitForQuickBuy(signal, timeoutMs = 8000) {
      const ready = window.jcWishlistQuickBuyReady;
      if (!ready) throw new Error('Wishlist Quick Buy is unavailable.');
      let timer;
      let abort;
      const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error('Wishlist Quick Buy did not initialize.')), timeoutMs);
      });
      const cancelled = new Promise((resolve, reject) => {
        abort = () => {
          const error = new Error('Wishlist Quick Buy initialization was cancelled.');
          error.name = 'AbortError';
          reject(error);
        };
        if (signal.aborted) abort();
        else signal.addEventListener('abort', abort, { once: true });
      });
      try {
        await Promise.race([ready, timeout, cancelled]);
        if (!window.customElements?.get('wishlist-quick-buy-modal')) throw new Error('Wishlist Quick Buy is unavailable.');
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
      }
    }

    namespace(root, prefix) {
      const elements = [root, ...root.querySelectorAll('*')];
      const ids = new Map(elements.filter((element) => element.hasAttribute('id'))
        .map((element) => [element.getAttribute('id'), `${prefix}-${element.getAttribute('id')}`]));
      for (const element of elements) {
        if (element.hasAttribute('id')) element.setAttribute('id', ids.get(element.getAttribute('id')));
        for (const attribute of ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'aria-owns']) {
          if (element.hasAttribute(attribute)) element.setAttribute(attribute,
            element.getAttribute(attribute).split(/\s+/).map((id) => ids.get(id) || id).join(' '));
        }
        if (element.matches('input[type="radio"][name]')) element.name = `${prefix}-${element.name}`;
      }
    }

    removeButton(item, title) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wishlist__remove link';
      button.dataset.wishlistRemove = item.id;
      button.setAttribute('aria-label', this.text('removeLabel', { title }));
      button.textContent = this.text('remove');
      return button;
    }

    heartButton(item, title) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wishlist__heart';
      button.dataset.wishlistRemove = item.id;
      button.setAttribute('aria-label', this.text('removeLabel', { title }));
      button.setAttribute('aria-pressed', 'true');
      button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z"/></svg>';
      return button;
    }

    placeholder(item, message) {
      const wrapper = document.createElement('div');
      wrapper.className = 'wishlist__item wishlist__item--unavailable';
      wrapper.dataset.wishlistItem = item.id;
      const notice = document.createElement('p');
      notice.textContent = message;
      wrapper.append(notice, this.removeButton(item, this.text('unavailable')));
      return wrapper;
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || this.busy) return;
      if (button.matches('[data-wishlist-retry], [data-wishlist-refresh]')) this.refresh();
      if (button.hasAttribute('data-wishlist-remove')) this.removeItem(button.dataset.wishlistRemove, button);
      if (button.hasAttribute('data-wishlist-audience')) {
        this.audience = button.dataset.wishlistAudience;
        this.page = 1;
        this.changePage();
      }
      if (button.hasAttribute('data-wishlist-previous')) { this.page -= 1; this.changePage(); }
      if (button.hasAttribute('data-wishlist-next')) { this.page += 1; this.changePage(); }
      for (const action of ['create', 'rename', 'delete']) if (button.hasAttribute(`data-wishlist-${action}`)) this.openDialog(action, button);
      if (button.hasAttribute('data-wishlist-dialog-cancel')) {
        this.dialogAction = null;
        this.querySelector('[data-wishlist-dialog]').close();
        this.dialogTrigger?.focus();
      }
    }

    changePage() {
      this.readAbort?.abort();
      this.readAbort = new AbortController();
      this.generation += 1;
      this.renderAudiences();
      const generation = this.generation;
      this.renderPage(generation).catch(() => {
        if (generation === this.generation) this.state('error', this.text('loadError'));
      });
    }

    async mutation(action, requiresList = true) {
      this.busy = true;
      const listId = this.selectedId;
      this.invalidate();
      this.state('loading');
      try {
        const sdk = await loadSDK();
        const client = sdk.init({ mode: 'theme' });
        const lists = await WishlistPage.readLists(client);
        if (requiresList && !lists.some((list) => list.id === listId)) throw new WishlistListConflictError();
        await timeout(action(client));
      } finally {
        this.busy = false;
        // Reconcile even a timeout; never blindly replay an uncertain mutation.
        await this.refresh();
      }
      if (this.dataset.wishlistState === 'guest') throw new WishlistGuestError();
      if (this.dataset.wishlistState === 'error') throw new Error('reconciliation');
    }

    async removeItem(id, button) {
      const item = this.items.find((entry) => entry.id === id);
      if (!item) return;
      const listId = item.listId;
      const nextId = this.items[this.items.indexOf(item) + 1]?.id || this.items[this.items.indexOf(item) - 1]?.id;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        await this.mutation((client) => client.removeItem(listId, id));
        this.querySelector('[data-wishlist-status]').textContent = this.text('removed');
        const nextButton = Array.from(this.querySelectorAll('[data-wishlist-remove]')).find((entry) => entry.dataset.wishlistRemove === nextId);
        (nextButton || this.querySelector('[data-wishlist-lists]')).focus();
      } catch (error) {
        if (error instanceof WishlistGuestError) { this.invalidate(); this.state('guest'); }
        else {
          this.querySelector('[data-wishlist-status]').textContent = this.text('mutationError');
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }
    }

    openDialog(action, trigger) {
      const dialog = this.querySelector('[data-wishlist-dialog]');
      this.dialogAction = action;
      this.dialogTrigger = trigger;
      this.querySelector('[data-wishlist-dialog-title]').textContent = this.text(`${action}Title`);
      this.querySelector('[data-wishlist-dialog-description]').textContent = action === 'delete' ?
        this.text('deleteDescription', { title: this.lists.find((list) => list.id === this.selectedId)?.name || '' }) : '';
      this.querySelector('[data-wishlist-dialog-description]').hidden = action !== 'delete';
      this.querySelector('[data-wishlist-dialog-error]').textContent = '';
      this.querySelector('[data-wishlist-dialog-error]').hidden = true;
      this.querySelector('[data-wishlist-name-field]').hidden = action === 'delete';
      const input = this.querySelector('[data-wishlist-name]');
      input.disabled = action === 'delete';
      input.required = action !== 'delete';
      input.value = action === 'rename' ? this.lists.find((list) => list.id === this.selectedId)?.name || '' : '';
      this.querySelector('[data-wishlist-dialog-submit]').textContent = this.text(action === 'delete' ? 'delete' : 'save');
      dialog.showModal();
      (action === 'delete' ? this.querySelector('[data-wishlist-dialog-cancel]') : input).focus();
    }

    async mutateList() {
      if (this.busy) return;
      const action = this.dialogAction;
      const listId = this.selectedId;
      const name = this.querySelector('[data-wishlist-name]').value.trim();
      if (action !== 'delete' && !name) {
        this.querySelector('[data-wishlist-dialog-error]').textContent = this.text('nameRequired');
        this.querySelector('[data-wishlist-dialog-error]').hidden = false;
        return;
      }
      const submit = this.querySelector('[data-wishlist-dialog-submit]');
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      try {
        await this.mutation(async (client) => {
          if (action === 'create') {
            const result = await client.createWishlist(name);
            this.preferredListName = name;
            return result;
          }
          if (action === 'rename') return client.renameWishlist(listId, name);
          return client.deleteWishlist(listId);
        }, action !== 'create');
        this.dialogAction = null;
        this.querySelector('[data-wishlist-dialog]').close();
        this.dialogTrigger?.focus();
      } catch (error) {
        if (error instanceof WishlistGuestError) {
          this.querySelector('[data-wishlist-dialog]').close();
          this.invalidate();
          this.state('guest');
        } else {
          this.querySelector('[data-wishlist-dialog-error]').textContent = this.text('mutationError');
          this.querySelector('[data-wishlist-dialog-error]').hidden = false;
        }
      } finally {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      }
    }
  }

  if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
})();
